/**
 * Turns Ember primitives into one pixel-art frame.
 *
 * An orthographic camera pitched down by `pitch` looks at the body; each pixel's ray keeps the
 * nearest surface. Shading is banded into the material's ramp from one fixed light (upper left,
 * like the LPC art), the silhouette gets the material's outline and a surface that stands in front
 * of another gets a crease line. No antialiasing: every pixel is a palette colour or transparent.
 *
 * Frame space matches LPC: a 64px cell, the body centred on x = 32 with its soles on row 61, so an
 * Ember frame drops into the same placement, shadow and ground-anchor code.
 */
import { PartGroup, Primitive } from "./body"
import { Material } from "./palette"
import { Vec3, apply, columns, deg, dot, mul, normalize, rotY } from "./vec"

export type Facing = "front" | "back" | "left" | "right" | "frontleft" | "frontright" | "backleft" | "backright"
export const FACINGS: Facing[] = ["front", "back", "left", "right", "frontleft", "frontright", "backleft", "backright"]

/** Clockwise screen directions, starting at east. Kept separate from LPC's four sheet rows. */
const SCREEN_FACINGS: Facing[] = ["right", "frontright", "front", "frontleft", "left", "backleft", "back", "backright"]
export const emberFacingAt = (angle: number): Facing => {
    const sector = Math.round(angle / (Math.PI / 4))
    return SCREEN_FACINGS[((sector % 8) + 8) % 8]
}

/** The screen direction a facing stands for (radians, clockwise from screen right): emberFacingAt's inverse. */
export const facingScreenAngle = (facing: Facing): number => SCREEN_FACINGS.indexOf(facing) * (Math.PI / 4)

export const isBackFacing = (facing: string): boolean => facing.startsWith("back")

/**
 * How far a side facing's BODY is turned toward the camera. LPC's side figures stand about this far
 * round: the near shoulder caps the back of the torso with its arm hanging down the back, the far
 * hand peeks out in front of the belly, and the far leg is the forward one. The head does not turn
 * with it (HEAD_COUNTER_TURN): a face turned this far shows its featureless front.
 */
export const SIDE_TURN = (35 * Math.PI) / 180

/**
 * Where a side-facing body stands in its frame, in pixels toward the way it faces (x) and up (y),
 * so its silhouette lands on the LPC body's: turned three-quarters on, the body's centre of mass
 * is not where a front-on one's is.
 */
export const SIDE_OFFSET = { x: -1, y: 0 }

/** How much of the body's turn the head takes back, toward a profile, in a side facing. */
export const HEAD_COUNTER_TURN = (25 * Math.PI) / 180

/** Yaw that turns model +z (the way the body faces) toward each screen facing. */
export const FACING_YAW: Record<Facing, number> = {
    front: 0,
    frontright: Math.PI / 4,
    frontleft: -Math.PI / 4,
    backright: 3 * Math.PI / 4,
    backleft: -3 * Math.PI / 4,
    right: Math.PI / 2 - SIDE_TURN,
    back: Math.PI,
    left: -(Math.PI / 2 - SIDE_TURN),
}

/**
 * The share of a step that crosses the screen in the direction the body travels: all of it from
 * the side, cos(SIDE_TURN) of it once the body is turned toward the camera. Front and back keep the
 * whole stride (their feet move up and down the screen, foreshortened, as LPC's do).
 */
export const STRIDE_ACROSS_SCREEN: Record<Facing, number> = {
    front: 1,
    // At 45 degrees both the projected step and screen travel have a sqrt(1/2) x component.
    frontright: 1,
    frontleft: 1,
    backright: 1,
    backleft: 1,
    back: 1,
    left: Math.cos(SIDE_TURN),
    right: Math.cos(SIDE_TURN),
}

export interface RasterOptions {
    width: number
    height: number
    /** Screen x of the body's midline. */
    centerX: number
    /** Screen y of the ground plane under the pelvis (pixel rows above it are drawn). */
    groundY: number
    pitch: number
    light: Vec3
    /** World pixels a part must stand in front of one it flows into before the seam is drawn. */
    creaseDepth: number
    /**
     * Outline the body against an arm behind it, too, not only an arm in front of the body: an
     * archer's drawing arm reaching from the far shoulder across the chest to the string comes out
     * from behind the chest, and unlined it reads as part of the shirt (clips.ts bowStream).
     */
    armsBehindOutlined?: boolean
    /**
     * `dark` (LPC's): every contour in the outline colour. `selective`: the silhouette's edge turned
     * toward the light is drawn in the softer line colour, and so is a limb's contour across the body,
     * leaving the darkest colour for the shadowed edge -- the line follows the light, not one ink.
     */
    outline?: "dark" | "selective" | "soft"
    /**
     * How LPC paints a volume: the weights of light and of facing the viewer in a pixel's darkness,
     * the extra darkness of the rim just inside the outline, and the share of each ramp step
     * (light, base, shade, deep) -- read off the LPC male body.
     */
    shading: {
        light: number
        facing: number
        rim: number
        mix: Record<Region, number[]>
        /**
         * Darkness at which light gives way to base, base to shade, shade to deep. Set, these replace
         * `mix`: steps then follow the light itself, one clean terminator across the whole body,
         * instead of each region taking its LPC share of every step.
         */
        thresholds?: [number, number, number]
    }
    /** How far a limb segment must stand in front of the next (an elbow, a knee) before it is lined. */
    jointLine?: number
}

/** The parts LPC shades each in its own proportions: arms and legs far darker than the face. */
export type Region = "head" | "torso" | "arm" | "hand" | "leg" | "foot"
export const regionOf = (prim: Primitive): Region =>
    prim.group === "head" || prim.group === "hair"
        ? "head"
        : prim.group === "torso" || prim.group === "bust" || prim.group === "shoulder" || prim.group === "skirt" || prim.group === "tail"
          ? "torso"
          : prim.part === "hand" || prim.group === "held"
            ? "hand"
            : prim.part === "foot"
              ? "foot"
              : prim.group === "armL" || prim.group === "armR"
                ? "arm"
                : "leg"

export const DEFAULT_RASTER: RasterOptions = {
    width: 64,
    height: 64,
    centerX: 32,
    // The row under the soles: the LPC body's soles are drawn on row 61.
    groundY: 61,
    // Nearly straight on, as LPC draws characters. A steeper camera shows the top of the head at the
    // cost of the face, and draws anything further from it higher: the far foot of a side-on body
    // lifts off the ground line and reads as hooked.
    pitch: deg(12),
    light: normalize([-0.55, 0.75, 0.5]),
    // How far a part must stand in front of one it flows into (hips into thighs, chin over neck)
    // before that seam gets a line (see boundaryBetween; limbs are outlined regardless).
    creaseDepth: 1.2,
    // Light, base, shade, deep per part, measured off the LPC male body through Ember's parts in the
    // aligned standing pose (tools ember-partmix): a head is mostly base, legs mostly shade.
    shading: {
        light: 0.75,
        facing: 0.4,
        rim: 0.65,
        mix: {
            head: [0.05, 0.68, 0.2, 0.07],
            torso: [0.09, 0.45, 0.34, 0.12],
            arm: [0.1, 0.38, 0.31, 0.21],
            hand: [0.06, 0.65, 0.14, 0.15],
            leg: [0, 0.27, 0.46, 0.27],
            foot: [0.12, 0.5, 0.24, 0.14],
        },
    },
}

/**
 * Pixel art laid over the render at 1:1: features too small to survive being rendered from
 * geometry (an eye is 4x3 pixels), drawn the way the LPC art draws them. `rows` are characters
 * looked up in `palette`; "." leaves the render alone. Only pixels whose surface belongs to `group`
 * are painted, so a hand or a fringe in front of the face still covers the eyes.
 */
export type PartGroupList = PartGroup | PartGroup[]

export interface PixelStamp {
    /** Top-left of rows[0][0], in frame pixels. */
    x: number
    y: number
    rows: string[]
    palette: Record<string, number>
    /** The part, or parts, it may paint over. */
    group: PartGroupList
    /** Paint only over this material (bare skin), so clothing over the spot hides the stamp. */
    material?: Material
}

export interface Frame {
    width: number
    height: number
    /** RGBA, row-major, unpremultiplied (alpha is only ever 0 or 255). */
    pixels: Uint8ClampedArray
    /** Top row of the visible head/hair, excluding raised hands and tools. */
    headTop?: number
}

/** Turn model-space primitives to a facing. */
export const facePrimitives = (prims: Primitive[], facing: Facing): Primitive[] => {
    const yaw = rotY(FACING_YAW[facing])
    return prims.map(prim =>
        prim.kind === "capsule"
            ? { ...prim, a: apply(yaw, prim.a), b: apply(yaw, prim.b) }
            : // Rotating the basis rotates each of its column axes.
              { ...prim, center: apply(yaw, prim.center), basis: mul(yaw, prim.basis) },
    )
}

/**
 * The ramp step lighting picks: 0 light, 1 base, 2 shade, 3 deep. Flat on purpose: most of a body
 * is BASE, shade only where it turns from the light, a highlight only where it faces it squarely.
 * More steps follow every curve of the volumes underneath and the body melts; LPC paints a few
 * confident shapes instead.
 */
const bandIndex = (light: number): number => (light > 0.9 ? 0 : light > -0.12 ? 1 : light > -0.6 ? 2 : 3)
const rampStep = (m: Material, step: number): number => [m.light, m.base, m.shade, m.deep][Math.min(3, step)]

type Boundary = "outline" | "line"
const isArm = (g: PartGroup) => g === "armL" || g === "armR" || g === "held"
// A humanoid's legL and legR, and a species' legs (legFL, legHR...).
const isLeg = (g: PartGroup) => g.startsWith("leg")

/**
 * What LPC draws where one part of the body meets another, on the side of the part in front (`dz`,
 * this pixel's depth over its neighbour's, is positive). Limbs are outlined as shapes of their own:
 * an arm against the body, one leg against the other, hair against whatever it lies over. Parts
 * that flow into each other are not -- hips into thighs, the chin over the neck -- unless one
 * stands well in front, which gets the softer line colour. The shoulder bridges arm and chest and
 * is never outlined against either.
 */
const boundaryBetween = (here: PartGroup, there: PartGroup, dz: number, deep: number, armsBehind?: boolean): Boundary | undefined => {
    if (here === there) return undefined
    // The part in front takes the line. Two at the same depth -- thighs pressed together, front-on
    // and mirror-symmetric -- would each leave it to the other, so the first by name takes it.
    if (Math.abs(dz) < 1e-6 ? here > there : dz < 0) return undefined
    if (here === "shoulder" || there === "shoulder") return undefined
    // Hair, and a tail, are shapes of their own against whatever they lie over.
    if (here === "hair" || here === "tail") return "outline"
    if (isArm(here) && (there === "torso" || there === "bust" || isLeg(there) || isArm(there))) return "outline"
    if (armsBehind && (here === "torso" || here === "bust") && isArm(there)) return "outline"
    if (isLeg(here) && isLeg(there)) return "outline"
    // A skirt's hem over the legs it hangs round, and a knee pushed out in front of it.
    if ((here === "skirt" && isLeg(there)) || (isLeg(here) && there === "skirt")) return "outline"
    // Thighs into the pelvis: no seam at all. Traced, it runs hip to hip like a waistband; LPC marks
    // only the groin, with two short strokes (marks.ts).
    if ((isLeg(here) && there === "torso") || (here === "torso" && isLeg(there))) return undefined
    return dz > deep ? "line" : undefined
}

/** Camera axes for a pitch: screen right, screen up, and toward the viewer. */
export const cameraAxes = (pitch: number): { right: Vec3; up: Vec3; back: Vec3 } => ({
    right: [1, 0, 0],
    up: [0, Math.cos(pitch), -Math.sin(pitch)],
    back: [0, Math.sin(pitch), Math.cos(pitch)],
})

/** Where a world point lands on screen, in frame pixels. */
export const projectPoint = (p: Vec3, o: RasterOptions = DEFAULT_RASTER): { x: number; y: number } => {
    const { right, up } = cameraAxes(o.pitch)
    return { x: o.centerX + dot(p, right), y: o.groundY - dot(p, up) }
}

/** A foot on a frame, for effects at the ground it treads (grass parting round it). */
export interface FrameFoot {
    /** The ground under the sole, in frame pixels. */
    x: number
    y: number
    /** Frame pixels the sole is drawn above that ground: 0 while it is planted. */
    lift: number
    /** Bearing weight: planted, by the clip's say where it has one, else by being on the ground. */
    down: boolean
    /** Its leg hangs from the far side of the body -- a far hip, a hind leg seen from in front -- so the body hides it. */
    behind: boolean
}

/**
 * A leg's root this much further from the camera than the middle of all of them is behind the body: a hind leg
 * seen from in front. A far hip or a far flank's legs (a few px across) stay in view below the body.
 */
const BEHIND_DEPTH = 5
/** A sole lower than this many frame pixels off the ground is on it, for a clip that does not say. */
const DOWN_LIFT = 0.75

/**
 * Where a foot is on a frame. `sole` is the bottom of the foot in the body's ground frame (y up, the ground 0),
 * `root` the joint its leg hangs from and `middle` the middle of every leg's root; `turn` faces them the way the
 * frame does.
 */
export const frameFoot = (sole: Vec3, root: Vec3, middle: Vec3, turn: (v: Vec3) => Vec3, raster: RasterOptions, planted?: boolean): FrameFoot => {
    const s = turn(sole)
    const ground = projectPoint([s[0], 0, s[2]], raster)
    const lift = Math.max(0, ground.y - projectPoint(s, raster).y)
    return {
        x: ground.x,
        y: ground.y,
        lift,
        // A gait's swinging foot skims the ground mid-swing: only its clip knows it is not stepping there.
        down: planted ?? lift < DOWN_LIFT,
        behind: turn(root)[2] < turn(middle)[2] - BEHIND_DEPTH,
    }
}

const EPS = 1e-9

/** What shading needs from the surface a pixel kept. */
interface Shaded {
    light: number
    /** How squarely the surface faces the camera: 1 head-on, 0 at the silhouette. */
    facing: number
    decal?: number
}

// Primitives are moved into camera space -- x right, y up the screen, z toward the viewer -- where
// every pixel's ray is simply the line (u, v, z). The camera is orthographic, so everything that
// does not depend on the pixel is worked out once per primitive rather than per pixel.

const prepareCapsule = (prim: Extract<Primitive, { kind: "capsule" }>, cam: (p: Vec3) => Vec3, light: Vec3) => {
    const [ax, ay, az] = cam(prim.a)
    const [bx, by, bz] = cam(prim.b)
    const r = prim.r
    const r2 = r * r
    const bax = bx - ax, bay = by - ay, baz = bz - az
    const baba = bax * bax + bay * bay + baz * baz
    // Along the ray (0, 0, -1): the cylinder's quadratic has a constant leading term.
    const k = baba - baz * baz
    const sphereZ = (cx: number, cy: number, cz: number, u: number, v: number) => {
        const dx = u - cx, dy = v - cy
        const h = r2 - dx * dx - dy * dy
        return h > 0 ? cz + Math.sqrt(h) : NaN
    }
    const hit = (u: number, v: number): number => {
        const ox = u - ax, oy = v - ay
        let best = NaN
        if (k > EPS) {
            // Points (u, v, z): distance to the axis line is r. Solve for the larger z.
            // With w = z - az: |o + w e_z|^2 - ((o + w e_z)·ba)^2 / baba = r^2.
            const oba = ox * bax + oy * bay
            const qa = 1 - (baz * baz) / baba
            const qb = -(oba * baz) / baba
            const qc = ox * ox + oy * oy - (oba * oba) / baba - r2
            const h = qb * qb - qa * qc
            if (h >= 0) {
                const w = (-qb + Math.sqrt(h)) / qa
                const along = oba + w * baz
                if (along > 0 && along < baba) best = az + w
            }
        }
        if (best !== best) {
            const za = sphereZ(ax, ay, az, u, v)
            const zb = sphereZ(bx, by, bz, u, v)
            best = za !== za ? zb : zb !== zb ? za : Math.max(za, zb)
        }
        return best
    }
    const shade = (u: number, v: number, z: number): Shaded => {
        const px = u - ax, py = v - ay, pz = z - az
        const t = baba > EPS ? Math.max(0, Math.min(1, (px * bax + py * bay + pz * baz) / baba)) : 0
        const nx = px - bax * t, ny = py - bay * t, nz = pz - baz * t
        const l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1
        return { light: (nx * light[0] + ny * light[1] + nz * light[2]) / l, facing: nz / l }
    }
    const reach = Math.sqrt(baba) / 2 + r
    const cx = (ax + bx) / 2, cy = (ay + by) / 2
    return { hit, shade, cx, cy, reach }
}

const prepareEllipsoid = (prim: Extract<Primitive, { kind: "ellipsoid" }>, cam: (p: Vec3) => Vec3, light: Vec3) => {
    const ex = prim.exponent
    if (ex !== undefined && (typeof ex === "number" ? ex !== 2 : ex.some(e => e !== 2))) return prepareRoundedBox(prim, cam, light)
    const [cx, cy, cz] = cam(prim.center)
    // The ellipsoid's axes in camera space, each divided by its radius squared for the quadratic.
    const axes = columns(prim.basis).map(cam)
    const [r0, r1, r2] = prim.radii
    const inv = [1 / (r0 * r0), 1 / (r1 * r1), 1 / (r2 * r2)]
    const A = axes.reduce((sum, e, i) => sum + e[2] * e[2] * inv[i], 0)
    let lastQ: Vec3 = [0, 0, 0]
    let lastSign = 1
    const local = (du: number, dv: number, w: number): Vec3 => [
        du * axes[0][0] + dv * axes[0][1] + w * axes[0][2],
        du * axes[1][0] + dv * axes[1][1] + w * axes[1][2],
        du * axes[2][0] + dv * axes[2][1] + w * axes[2][2],
    ]
    const hit = (u: number, v: number): number => {
        const du = u - cx, dv = v - cy
        const a0 = du * axes[0][0] + dv * axes[0][1]
        const a1 = du * axes[1][0] + dv * axes[1][1]
        const a2 = du * axes[2][0] + dv * axes[2][1]
        const B = a0 * axes[0][2] * inv[0] + a1 * axes[1][2] * inv[1] + a2 * axes[2][2] * inv[2]
        const C = a0 * a0 * inv[0] + a1 * a1 * inv[1] + a2 * a2 * inv[2] - 1
        const h = B * B - A * C
        if (h < 0) return NaN
        const root = Math.sqrt(h)
        // The outside of the shell first; if a clip removes it, the inside may still face the camera.
        for (const sign of [1, -1]) {
            const w = (-B + sign * root) / A
            const q = local(du, dv, w)
            if (prim.keep && !prim.keep(q)) continue
            lastQ = q
            lastSign = sign
            return cz + w
        }
        return NaN
    }
    const shade = (_u: number, _v: number, _z: number): Shaded => {
        const q = lastQ
        let nx = 0, ny = 0, nz = 0
        for (let i = 0; i < 3; i++) {
            const s = q[i] * inv[i]
            nx += axes[i][0] * s
            ny += axes[i][1] * s
            nz += axes[i][2] * s
        }
        const l = (Math.sqrt(nx * nx + ny * ny + nz * nz) || 1) * lastSign
        return { light: (nx * light[0] + ny * light[1] + nz * light[2]) / l, facing: nz / l, decal: prim.decal?.(q) }
    }
    return { hit, shade, cx, cy, reach: Math.max(r0, r1, r2) }
}

/**
 * A superellipsoid: no closed-form ray hit, but along any ray |x/a|^e + ... - 1 is a sum of convex
 * functions, so Newton's method from outside the shape walks monotonically onto the first surface
 * the ray meets (and from beyond it, onto the last) without overshooting. A handful of steps.
 */
const prepareRoundedBox = (prim: Extract<Primitive, { kind: "ellipsoid" }>, cam: (p: Vec3) => Vec3, light: Vec3) => {
    const [cx, cy, cz] = cam(prim.center)
    const axes = columns(prim.basis).map(cam)
    const r = prim.radii
    // One squareness per local axis: |x/a|^ex + |y/b|^ey + |z/c|^ez = 1, still convex for each >= 2.
    const ex = prim.exponent
    const e: number[] = typeof ex === "number" ? [ex, ex, ex] : ex
    // The shape lies inside its box, and the box inside this sphere.
    const R = Math.sqrt(r[0] * r[0] + r[1] * r[1] + r[2] * r[2])
    const B = [0, 1, 2].map(i => -axes[i][2] / r[i])
    const A = [0, 0, 0]
    let lastQ: Vec3 = [0, 0, 0]
    let lastSign = 1
    // Value and slope of the implicit function along the ray, at parameter t.
    const g = (t: number): [number, number] => {
        let value = -1, slope = 0
        for (let i = 0; i < 3; i++) {
            const q = A[i] + t * B[i]
            const m = Math.abs(q)
            const p1 = Math.pow(m, e[i] - 1)
            value += p1 * m
            slope += e[i] * p1 * (q < 0 ? -B[i] : B[i])
        }
        return [value, slope]
    }
    const solve = (t: number, forward: boolean): number => {
        for (let k = 0; k < 16; k++) {
            const [value, slope] = g(t)
            if (value < 1e-3) return t
            // Moving away from the shape (or level): this ray misses it.
            if (forward ? slope >= 0 : slope <= 0) return NaN
            t -= value / slope
            if (t < 0 || t > 2 * R) return NaN
        }
        return t
    }
    // In scaled coordinates the shape lies inside a ball of this radius (its corners are the
    // farthest points), so a ray that misses the ball misses the shape -- and one that meets it can
    // start Newton at the ball rather than far out, a few steps from the surface.
    const ball = Math.pow(3, 0.5 - 1 / Math.max(e[0], e[1], e[2]))
    const BB = B[0] * B[0] + B[1] * B[1] + B[2] * B[2]
    const at = (t: number): Vec3 => [(A[0] + t * B[0]) * r[0], (A[1] + t * B[1]) * r[1], (A[2] + t * B[2]) * r[2]]
    const hit = (u: number, v: number): number => {
        const du = u - cx, dv = v - cy
        for (let i = 0; i < 3; i++) A[i] = (du * axes[i][0] + dv * axes[i][1] + R * axes[i][2]) / r[i]
        const AB = A[0] * B[0] + A[1] * B[1] + A[2] * B[2]
        const AA = A[0] * A[0] + A[1] * A[1] + A[2] * A[2]
        const h = AB * AB - BB * (AA - ball * ball)
        if (h < 0) return NaN
        const root = Math.sqrt(h)
        const entry = solve(Math.max(0, (-AB - root) / BB), true)
        if (entry !== entry) return NaN
        let q = at(entry)
        if (!prim.keep || prim.keep(q)) {
            lastQ = q
            lastSign = 1
            return cz + R - entry
        }
        // Clipped away in front: the inside of the far wall may still face the camera.
        const exit = solve(Math.min(2 * R, (-AB + root) / BB), false)
        if (exit !== exit) return NaN
        q = at(exit)
        if (!prim.keep(q)) return NaN
        lastQ = q
        lastSign = -1
        return cz + R - exit
    }
    const shade = (_u: number, _v: number, _z: number): Shaded => {
        const q = lastQ
        let nx = 0, ny = 0, nz = 0
        for (let i = 0; i < 3; i++) {
            const s = (Math.sign(q[i]) * Math.pow(Math.abs(q[i]) / r[i], e[i] - 1)) / r[i]
            nx += axes[i][0] * s
            ny += axes[i][1] * s
            nz += axes[i][2] * s
        }
        const l = (Math.sqrt(nx * nx + ny * ny + nz * nz) || 1) * lastSign
        return { light: (nx * light[0] + ny * light[1] + nz * light[2]) / l, facing: nz / l, decal: prim.decal?.(q) }
    }
    return { hit, shade, cx, cy, reach: R }
}

export const rasterize = (prims: Primitive[], o: RasterOptions = DEFAULT_RASTER, stamps: PixelStamp[] = []): Frame => {
    const { width: W, height: H } = o
    const { right, up, back } = cameraAxes(o.pitch)
    const cam = (p: Vec3): Vec3 => [dot(p, right), dot(p, up), dot(p, back)]
    const light = cam(o.light)
    const depth = new Float32Array(W * H).fill(-Infinity)
    const owner = new Int16Array(W * H).fill(-1)
    const lightAt = new Float32Array(W * H)
    const facingAt = new Float32Array(W * H)
    const decal = new Int32Array(W * H).fill(-1)

    prims.forEach((prim, index) => {
        const prepared = prim.kind === "capsule" ? prepareCapsule(prim, cam, light) : prepareEllipsoid(prim, cam, light)
        const sx = o.centerX + prepared.cx
        const sy = o.groundY - prepared.cy
        const x0 = Math.max(0, Math.floor(sx - prepared.reach - 1))
        const x1 = Math.min(W - 1, Math.ceil(sx + prepared.reach + 1))
        const y0 = Math.max(0, Math.floor(sy - prepared.reach - 1))
        const y1 = Math.min(H - 1, Math.ceil(sy + prepared.reach + 1))
        for (let py = y0; py <= y1; py++) {
            const v = o.groundY - (py + 0.5)
            for (let px = x0; px <= x1; px++) {
                const u = px + 0.5 - o.centerX
                const z = prepared.hit(u, v)
                if (z !== z) continue
                const i = py * W + px
                if (z <= depth[i]) continue
                depth[i] = z
                owner[i] = index
                const shaded = prepared.shade(u, v, z)
                lightAt[i] = shaded.light
                facingAt[i] = shaded.facing
                decal[i] = shaded.decal ?? -1
            }
        }
    })

    const pixels = new Uint8ClampedArray(W * H * 4)
    const covered = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H && owner[y * W + x] >= 0
    // Spurs: a pixel joined to the body on one side only is the corner of a round volume just
    // reaching into the next row or column. A pixel artist would never leave one; neither do we.
    for (let pass = 0; pass < 2; pass++) {
        for (let y = 0; y < H; y++) {
            for (let x = 0; x < W; x++) {
                if (!covered(x, y)) continue
                const joined = (covered(x - 1, y) ? 1 : 0) + (covered(x + 1, y) ? 1 : 0) + (covered(x, y - 1) ? 1 : 0) + (covered(x, y + 1) ? 1 : 0)
                // A held thing's string or shaft is a deliberate one-pixel line, often diagonal: kept.
                if (joined <= 1 && prims[owner[y * W + x]].group !== "held") owner[y * W + x] = -1
            }
        }
    }
    // Slivers: a part showing one pixel wide at the silhouette, past the part in front of it (a far
    // arm just clearing the chest), would be drawn as its own outline beside that part's line -- a
    // doubled contour. LPC draws the near part's single outline there, so the pixel joins it.
    for (let y = 0; y < H; y++) {
        for (let x = 1; x < W - 1; x++) {
            const i = y * W + x
            if (owner[i] < 0) continue
            const [l, r] = [owner[i - 1], owner[i + 1]]
            const other = l < 0 && r >= 0 ? r : r < 0 && l >= 0 ? l : -1
            if (other >= 0 && prims[other].group !== prims[owner[i]].group) owner[i] = other
        }
    }
    // The silhouette: covered pixels with an empty 4-neighbour. They take the outline colour.
    const edge = new Uint8Array(W * H)
    for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
            if (covered(x, y) && (!covered(x - 1, y) || !covered(x + 1, y) || !covered(x, y - 1) || !covered(x, y + 1))) {
                edge[y * W + x] = 1
            }
        }
    }
    const nextToEdge = (x: number, y: number) =>
        edge[y * W + x - 1] === 1 || edge[y * W + x + 1] === 1 || edge[(y - 1) * W + x] === 1 || edge[(y + 1) * W + x] === 1
    const nearEdge = (x: number, y: number) =>
        (x >= 2 && edge[y * W + x - 2] === 1) || (x < W - 2 && edge[y * W + x + 2] === 1) ||
        (y >= 2 && edge[(y - 2) * W + x] === 1) || (y < H - 2 && edge[(y + 2) * W + x] === 1)
    const selective = o.outline === "selective"
    const soft = o.outline === "soft"
    // Whether an edge pixel's open side faces the light, on screen (y down).
    const litEdge = (x: number, y: number) => {
        const ox = (covered(x - 1, y) ? 0 : -1) + (covered(x + 1, y) ? 0 : 1)
        const oy = (covered(x, y - 1) ? 0 : -1) + (covered(x, y + 1) ? 0 : 1)
        return ox * light[0] - oy * light[1] > 0
    }
    const darkness = new Float32Array(W * H)
    // First the ramp step every shaded pixel takes (-1: a fixed colour -- outline, crease or decal).
    const step = new Int8Array(W * H).fill(-1)
    const fixed = new Int32Array(W * H)
    for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
            const i = y * W + x
            if (owner[i] < 0) continue
            const prim = prims[owner[i]]
            const m = prim.material
            if (edge[i]) {
                fixed[i] = soft ? (litEdge(x, y) ? m.shade : m.deep) : selective && litEdge(x, y) ? m.line : m.outline
                continue
            }
            if (decal[i] >= 0) {
                fixed[i] = decal[i]
                continue
            }
            // Where two parts meet, the one in front is outlined against the other, as LPC outlines
            // an arm against the chest or one leg against the other: a limb is drawn as its own
            // shape, not merged into whatever it touches. Well in front, it is the full outline;
            // barely in front, the softer line colour.
            let boundary: Boundary | undefined
            for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
                const j = ny * W + nx
                const other = prims[owner[j]]
                const b = boundaryBetween(prim.group, other.group, depth[i] - depth[j], o.creaseDepth, o.armsBehindOutlined) ??
                    // A joint: two segments of one limb, lined only where one overlaps the other --
                    // a partial stroke, as LPC marks a knee or an elbow, not a ring round the limb.
                    (other.group === prim.group && prim.part && other.part && other.part !== prim.part && prim.part !== "foot" && depth[i] - depth[j] > (o.jointLine ?? 1.0)
                        ? "line"
                        : undefined)
                if (b === "outline" || (b === "line" && !boundary)) boundary = b
            }
            if (boundary) {
                fixed[i] = soft ? m.deep : boundary === "outline" && !selective ? m.outline : m.line
                continue
            }
            // How dark LPC would paint this pixel: away from the light, turned from the viewer,
            // and near the outline -- LPC rims every volume, on both sides, not only the shadow side.
            darkness[i] = 1 - (o.shading.light * (lightAt[i] + 1)) / 2 - o.shading.facing * Math.max(0, facingAt[i]) +
                (nextToEdge(x, y) ? o.shading.rim : nearEdge(x, y) ? o.shading.rim / 2 : 0)
            step[i] = 1
        }
    }
    // Smoothed within each part, so the steps fall in clean regions rather than a speckle.
    const smooth = new Float32Array(W * H)
    for (let i = 0; i < W * H; i++) {
        if (step[i] < 0) continue
        const region = regionOf(prims[owner[i]])
        let sum = 0, n = 0
        for (const j of [i, i - 1, i + 1, i - W, i + W]) {
            if (j >= 0 && j < W * H && step[j] >= 0 && regionOf(prims[owner[j]]) === region) {
                sum += darkness[j]
                n++
            }
        }
        smooth[i] = sum / n
    }
    // Each part of each material is split into light/base/shade/deep in the proportions LPC paints
    // that part with, darkest pixels deepest: the mix matches the art by construction, any pose.
    const groups = new Map<string, number[]>()
    const materialIds = new Map<Material, number>()
    for (let i = 0; i < W * H; i++) {
        if (step[i] < 0) continue
        const prim = prims[owner[i]]
        if (!materialIds.has(prim.material)) materialIds.set(prim.material, materialIds.size)
        const key = `${materialIds.get(prim.material)}:${regionOf(prim)}`
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key)!.push(i)
    }
    const cuts = o.shading.thresholds
    if (cuts) {
        for (let i = 0; i < W * H; i++) {
            if (step[i] < 0) continue
            step[i] = smooth[i] < cuts[0] ? 0 : smooth[i] < cuts[1] ? 1 : smooth[i] < cuts[2] ? 2 : 3
        }
    }
    if (!cuts) groups.forEach((list, key) => {
        const mix = o.shading.mix[key.split(":")[1] as Region]
        list.sort((a, b) => smooth[a] - smooth[b])
        let k = 0
        mix.forEach((share, band) => {
            const end = band === mix.length - 1 ? list.length : k + Math.round(share * list.length)
            for (; k < end; k++) step[list[k]] = band
        })
    })
    // A highlight is a shape, not a sprinkle: a lit pixel with fewer than two lit neighbours is the
    // noise of a bumpy surface catching the light, and LPC would not have painted it.
    for (let i = 0; i < W * H; i++) {
        if (step[i] !== 0) continue
        const lit = (step[i - 1] === 0 ? 1 : 0) + (step[i + 1] === 0 ? 1 : 0) + (step[i - W] === 0 ? 1 : 0) + (step[i + W] === 0 ? 1 : 0)
        if (lit < 2) step[i] = 1
    }
    let headTop: number | undefined
    for (let i = 0; i < W * H; i++) {
        if (owner[i] < 0) continue
        const group = prims[owner[i]].group
        if (headTop === undefined && (group === "head" || group === "hair")) headTop = Math.floor(i / W)
        const color = step[i] >= 0 ? rampStep(prims[owner[i]].material, step[i]) : fixed[i]
        pixels[i * 4] = (color >> 16) & 255
        pixels[i * 4 + 1] = (color >> 8) & 255
        pixels[i * 4 + 2] = color & 255
        pixels[i * 4 + 3] = 255
    }
    for (const stamp of stamps) {
        stamp.rows.forEach((row, dy) => {
            for (let dx = 0; dx < row.length; dx++) {
                const color = stamp.palette[row[dx]]
                const x = stamp.x + dx, y = stamp.y + dy
                if (color === undefined || x < 0 || y < 0 || x >= W || y >= H) continue
                const i = y * W + x
                if (owner[i] < 0) continue
                const group = prims[owner[i]].group
                if (Array.isArray(stamp.group) ? !stamp.group.includes(group) : group !== stamp.group) continue
                if (stamp.material && prims[owner[i]].material !== stamp.material) continue
                // Never over an outline -- the silhouette's, or an arm's against the chest: a mark
                // drawn for LPC's limb would otherwise cut into Ember's where the two part by a pixel.
                if (edge[i] || (step[i] < 0 && fixed[i] === prims[owner[i]].material.outline)) continue
                pixels[i * 4] = (color >> 16) & 255
                pixels[i * 4 + 1] = (color >> 8) & 255
                pixels[i * 4 + 2] = color & 255
            }
        })
    }
    return { width: W, height: H, pixels, headTop }
}
